import {
  User,
  UserActivityStatus,
  UserAvailabilityStatus,
  UserSetAvailabilityStatus,
} from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import ServiceUser from '../types/ServiceUser';
import { EventAvailabilityState } from './eventAvailabilityState';
import { isIsoBefore } from '@lib/utils/time';
import { calculateConsolidatedStatus } from '@modules/UserStatusModule/utilities/ConsolidatedStatus/ConsolidatedStatus';

type UserAvailableResponse =
  | { isAvailable: true; isUnavailable: false; isOffline: false }
  | { isAvailable: false; isUnavailable: true; isOffline: false }
  | { isAvailable: false; isUnavailable: false; isOffline: true };

// Type for minimal user status data
type UserStatusData = {
  status: UserSetAvailabilityStatus;
  availabilityStatus: UserSetAvailabilityStatus;
  activityStatus: UserActivityStatus;
};

// Utility functions for ServiceUser
export const ServiceUserUtils = {
  isUserPhase2(): boolean {
    return FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureFeUserStatusPhase2,
    );
  },

  setAvailabilityStatus(user: ServiceUser, status: UserSetAvailabilityStatus) {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      user.availabilityStatus = status;
    } else {
      user.status = status;
    }
  },

  getAvailabilityStatus(userStatus: UserStatusData): UserSetAvailabilityStatus {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      return userStatus.availabilityStatus ?? userStatus.status; // Backward compatibility
    } else {
      return userStatus.status ?? userStatus.availabilityStatus; // Backward compatibility
    }
  },

  isUserAvailable(user: ServiceUser): boolean {
    return (
      this.getAvailabilityStatus(user) === UserSetAvailabilityStatus.Available
    );
  },

  /*
   * This will be deprecated once featureFeUserStatusPhase2 is fully rolled out
   */
  getCanChange(user: ServiceUser): boolean {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      return true;
    } else {
      return user.canChangeStatus ?? true; // Backward compatibility
    }
  },

  /*
   * This will be deprecated once featureFeUserStatusPhase2 is fully rolled out
   */
  setCanChange(user: ServiceUser, canChange: boolean) {
    if (
      !FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      user.canChangeStatus = canChange;
    }
  },

  getActivityStatus(userStatus: ServiceUser): UserActivityStatus {
    return (
      userStatus.activityStatus ??
      convertEventAvailabilityStateToActivity(userStatus.availabilityState) // Backward compatibility
    );
  },

  setActivityStatus(user: ServiceUser, activityStatus: UserActivityStatus) {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      user.activityStatus = activityStatus;
    }
  },

  setOriginalStatus(
    user: ServiceUser,
    originalStatus: UserSetAvailabilityStatus,
  ) {
    if (
      !FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      user.originalStatus = originalStatus;
    }
  },

  isWrapUp(user: ServiceUser): boolean {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      return user.activityStatus
        ? user.activityStatus === UserActivityStatus.WrapUp
        : user.originalStatus === UserAvailabilityStatus.WrapUp; // Backward compatibility
    }
    return user.originalStatus
      ? user.originalStatus === UserAvailabilityStatus.WrapUp
      : user.activityStatus === UserActivityStatus.WrapUp; // Backward compatibility
  },

  setEventActivityStatus(
    user: ServiceUser,
    eventAvailabilityState: string,
    setActivityStatus: boolean = true,
  ) {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      if (setActivityStatus) {
        user.activityStatus = convertEventAvailabilityStateToActivity(
          eventAvailabilityState,
        );
      }
    } else {
      user.availabilityState = eventAvailabilityState;
    }
  },

  parseAvailabilityActivityStatus(user: User) {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      if (
        user.availabilityActivityStatus?.availabilityStatus &&
        user.availabilityActivityStatus?.activityStatus
      ) {
        return {
          activityStatus: user.availabilityActivityStatus.activityStatus,
          availabilityStatus: userStatusToUserSetStatus(
            user.availabilityActivityStatus.availabilityStatus,
          ),
        };
      } else {
        console.error(
          'parseAvailabilityActivityStatus isUserStatusPhase2Enabled is On ,but availabilityActivityStatus is not returned from the query, user ',
          user,
        );
        return {
          activityStatus: UserActivityStatus.None,
          availabilityStatus: UserSetAvailabilityStatus.Offline,
        };
      }
    }
    return {
      status: userStatusToUserSetStatus(user.status),
      availabilityState: '',
      originalStatus: user.status,
      canChangeStatus: canChangeStatus(user.status), // canChangeStatus is deprecated from featureFeUserStatusPhase2, using only for previous status
    };
  },

  // Accepts the minimal shape needed from either GraphQL User or CurrentUser
  parseAvailabilityStatus(user: {
    status?: UserAvailabilityStatus;
    availabilityActivityStatus?: {
      availabilityStatus?: UserAvailabilityStatus | null;
      activityStatus?: UserActivityStatus | null;
    } | null;
  }) {
    const rawStatus = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureFeUserStatusPhase2,
    )
      ? user.availabilityActivityStatus?.availabilityStatus
      : user.status;
    const status = rawStatus ?? UserAvailabilityStatus.Offline;
    return userStatusToUserSetStatus(status);
  },

  getUserCapacity(user: ServiceUser): number {
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureFeUserStatusPhase2)
    ) {
      return user.activityStatus === UserActivityStatus.OnACall ||
        user.activityStatus === UserActivityStatus.WrapUp
        ? 0
        : 1;
    }
    return user.availabilityState === EventAvailabilityState.OnACall ||
      user.availabilityState === EventAvailabilityState.WrapupTime
      ? 0
      : 1;
  },

  /**
   * Checks if a service user is available
   * @param user - The service user to check
   * @returns The availability status of the service user as type {@link UserAvailableResponse}.
   *          Contains a boolean flag indicating if the user is available, unavailable, and offline (these are all mutually exclusive)
   */
  computeUserAvailability(user: ServiceUser): UserAvailableResponse {
    const status = ServiceUserUtils.getAvailabilityStatus(user);
    const activity = ServiceUserUtils.getActivityStatus(user);

    const isAvailable =
      status === UserSetAvailabilityStatus.Available &&
      (activity === UserActivityStatus.None ||
        activity === UserActivityStatus.CallOffer);

    if (isAvailable) {
      return {
        isUnavailable: false,
        isOffline: false,
        isAvailable: true,
      };
    }

    const isOffline = status === UserSetAvailabilityStatus.Offline;
    if (isOffline) {
      return {
        isUnavailable: false,
        isAvailable: false,
        isOffline: true,
      };
    }

    return {
      isAvailable: false,
      isUnavailable: true,
      isOffline: false,
    };
  },

  getUserConsolidatedStatus(user: ServiceUser) {
    return calculateConsolidatedStatus(user);
  },

  isCallOffered(user: ServiceUser): boolean {
    return (
      ServiceUserUtils.getActivityStatus(user) === UserActivityStatus.CallOffer
    );
  },
};

export const convertEventAvailabilityStateToActivity = (
  eventState: string,
): UserActivityStatus => {
  switch (eventState) {
    case EventAvailabilityState.OnACall:
      return UserActivityStatus.OnACall;
    case EventAvailabilityState.WrapupTime:
      return UserActivityStatus.WrapUp;
    case EventAvailabilityState.CallOffer:
      return UserActivityStatus.CallOffer;
    case EventAvailabilityState.OnExternalCall:
      return UserActivityStatus.OnExternalCall;
    default:
      return UserActivityStatus.None;
  }
};

export function canChangeStatus(status: UserAvailabilityStatus): boolean {
  if (
    status === UserAvailabilityStatus.OnACall ||
    status === UserAvailabilityStatus.CallOffer
  ) {
    return false;
  }
  return true;
}

export function userStatusToUserSetStatus(
  status: UserAvailabilityStatus,
): UserSetAvailabilityStatus {
  switch (status) {
    case UserAvailabilityStatus.CallOffer:
    case UserAvailabilityStatus.OnACall:
    case UserAvailabilityStatus.WrapUp:
    case UserAvailabilityStatus.Available:
      return UserSetAvailabilityStatus.Available;
    case UserAvailabilityStatus.AwayFromDesk:
      return UserSetAvailabilityStatus.AwayFromDesk;
    case UserAvailabilityStatus.Break:
      return UserSetAvailabilityStatus.Break;
    case UserAvailabilityStatus.Offline:
      return UserSetAvailabilityStatus.Offline;
    case UserAvailabilityStatus.ExtendedWrapUp:
      return UserSetAvailabilityStatus.ExtendedWrapUp;
    case UserAvailabilityStatus.Unavailable:
      return UserSetAvailabilityStatus.Unavailable;
  }
}

/**
 * Updates `user.stats.lastActionAt` only when the provided `timestamp` is later
 * than the user's current `lastActionAt` value.
 *
 * Behavior:
 * - No-op if `user.stats?.lastActionAt` is absent.
 * - No-op if `timestamp` is falsy or not later than the current `lastActionAt`.
 * - Mutates `user.stats.lastActionAt` in place when `timestamp` is a valid ISO string
 *   that represents a later instant.
 *
 * @param user - The `ServiceUser` whose stats will be updated in place.
 * @param timestamp - ISO-8601 timestamp (string) derived from the current event/action.
 * @returns void
 */
export const userStatsLastActionTimeFix = ({
  user,
  timestamp,
}: {
  user: ServiceUser;
  timestamp: string;
}) => {
  if (!user.stats?.lastActionAt) return;

  const { lastActionAt } = user.stats;

  // if current action timestamp is later than what we have, update it
  // The fix due to events don't have latest lastActionTime https://servicetitan.atlassian.net/browse/CCP-16049
  if (timestamp && isIsoBefore(lastActionAt, timestamp)) {
    user.stats.lastActionAt = timestamp;
  }
};
